import { Controller } from "stimulus"
import { showLoader, hideLoader } from "../utils/loader"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static values = {
    storageKey: String,
    url: String,
    method: String,
    resourceName: String,
    paramName: String,
    confirmMessage: String,
    redirectUrl: String
  }

  connect() {
    this.updateButtonState()
    this.selectionChangedHandler = this.handleSelectionChange.bind(this)
    document.addEventListener("selection:changed", this.selectionChangedHandler)
  }

  disconnect() {
    document.removeEventListener("selection:changed", this.selectionChangedHandler)
  }

  handleSelectionChange(event) {
    if (!event?.detail) {
      this.updateButtonState()
      return
    }

    const resourceFromEvent = event.detail.resourceName
    if (!this.hasResourceNameValue || resourceFromEvent === this.resourceNameValue) {
      this.updateButtonState()
      this.syncCurrentPageMetadata()
    }
  }

  updateButtonState() {
    const count = this.selectedIds().length
    this.element.disabled = count === 0
    this.element.classList.toggle("disabled", count === 0)
    this.element.classList.toggle("d-none", count === 0)
  }

  async submit(event, skipConfirm = false) {
    event.preventDefault()

    const ids = this.selectedIds()
    if (ids.length === 0) {
      alert("Please select at least one item.")
      return
    }

    if (!skipConfirm) {
      const message = this.confirmMessage(ids.length)
      if (message && !confirm(message)) {
        return
      }
    }

    showLoader()

    const formData = new FormData()
    const param = this.paramNameValue || `${this.resourceNameValue || "item"}_ids[]`
    ids.forEach(id => formData.append(param, id))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = csrfToken ? { "X-CSRF-Token": csrfToken } : {}

    try {
      const response = await fetch(this.urlValue, {
        method: this.methodValue ? this.methodValue.toUpperCase() : "POST",
        headers,
        body: formData
      })

      const successful = response.ok || (response.status >= 200 && response.status < 400) || response.redirected
      if (!successful) {
        const text = await response.text()
        throw new Error(text || `Request failed with status ${response.status}`)
      }

      this.clearSelection()
      const targetUrl = this.hasRedirectUrlValue && this.redirectUrlValue
        ? this.redirectUrlValue
        : (response.redirected && response.url ? response.url : null)

      if (targetUrl) {
        const resolvedTarget = new URL(targetUrl, window.location.href).href
        const currentBase = window.location.href.split("#")[0]
        const targetBase = resolvedTarget.split("#")[0]

        if (currentBase === targetBase) {
          window.location.href = resolvedTarget
          window.location.reload()
        } else {
          window.location.assign(resolvedTarget)
        }
      } else {
        window.location.reload()
      }
    } catch (error) {
      alert(error.message || "Request failed.")
    } finally {
      hideLoader()
    }
  }

  selectedIds() {
    try {
      const stored = sessionStorage.getItem(this.storageKeyValue)
      const ids = stored ? JSON.parse(stored) : []
      return Array.isArray(ids) ? ids : []
    } catch {
      return []
    }
  }

  clearSelection() {
    try {
      sessionStorage.removeItem(this.storageKeyValue)
      sessionStorage.removeItem(`${this.storageKeyValue}_metadata`)
    } catch {
      // no-op
    }
  }

  confirmMessage(count) {
    const resource = this.resourceNameValue || "item"
    if (this.hasConfirmMessageValue && this.confirmMessageValue) {
      return this.confirmMessageValue.replace("%{count}", count).replace("%{resource}", resource)
    }
    return `Are you sure you want to process ${count} selected ${resource}(s)?`
  }

  openModal(event) {
    event.preventDefault()

    const ids = this.selectedIds()
    if (ids.length === 0) {
      alert("Please select at least one item.")
      return
    }
  }

  openGuardrailModal(event) {
    if (event) event.preventDefault()

    this.syncCurrentPageMetadata()

    const ids = this.selectedIds()
    if (ids.length === 0) {
      alert("Please select at least one organization.")
      return
    }

    const modalElement = document.getElementById("bulkGuardrailModal")
    if (!modalElement) {
      this.submit(event, true)
      return
    }

    const actionType = (event?.currentTarget?.dataset.selectedBulkActionTypeValue || "deactivate").toLowerCase()
    const isDelete = actionType === "delete"
    const keyword = isDelete ? "DELETE" : "DEACTIVATE"

    const metadata = this.storedMetadata()
    let totalUsers = 0
    const orgItems = []

    ids.forEach(id => {
      const stringId = String(id)
      const cached = metadata[stringId]

      let name = `Organization #${id}`
      let usersCount = 0

      if (cached) {
        name = cached.name
        usersCount = cached.usersCount
      } else {
        const row = document.querySelector(`tr[data-test-id='${id}']`) || document.querySelector(`tr[data-organization-id='${id}']`)
        if (row) {
          name = row.dataset.organizationName || row.querySelector("td a")?.textContent?.trim() || name
          usersCount = parseInt(row.dataset.usersCount || row.children[2]?.textContent?.trim() || "0", 10) || 0
        }
      }

      totalUsers += usersCount
      orgItems.push({ id, name, usersCount })
    })

    const titleEl = modalElement.querySelector("[data-bulk-guardrail-title]")
    const actionTextEls = modalElement.querySelectorAll("[data-bulk-guardrail-action-text]")
    const countEl = modalElement.querySelector("[data-bulk-guardrail-count]")
    const totalUsersEl = modalElement.querySelector("[data-bulk-guardrail-total-users]")
    const usersSummaryEl = modalElement.querySelector("[data-bulk-guardrail-users-summary]")
    const deleteWarningEl = modalElement.querySelector("[data-bulk-guardrail-delete-warning]")
    const keywordLabel = modalElement.querySelector("[data-bulk-guardrail-keyword-label]")
    const listContainer = modalElement.querySelector("[data-bulk-guardrail-items-list]")
    const confirmInput = modalElement.querySelector("[data-bulk-guardrail-input]")
    const confirmBtn = modalElement.querySelector("[data-bulk-guardrail-confirm]")

    if (titleEl) titleEl.textContent = isDelete ? "Bulk Delete Organizations" : "Bulk Deactivate Organizations"
    actionTextEls.forEach(el => el.textContent = isDelete ? "delete" : "deactivate")
    if (countEl) countEl.textContent = ids.length
    if (totalUsersEl) totalUsersEl.textContent = totalUsers
    if (usersSummaryEl) usersSummaryEl.textContent = `Total active users: ${totalUsers}`
    if (deleteWarningEl) deleteWarningEl.classList.toggle("d-none", !isDelete)
    if (keywordLabel) keywordLabel.textContent = keyword

    if (listContainer) {
      listContainer.innerHTML = ""
      orgItems.forEach(item => {
        const li = document.createElement("li")
        li.className = "list-group-item d-flex justify-content-between align-items-center py-2"

        const nameSpan = document.createElement("span")
        nameSpan.className = "text-truncate me-2 fw-medium"
        nameSpan.textContent = item.name

        const badgeSpan = document.createElement("span")
        badgeSpan.className = "badge bg-light text-dark border"
        badgeSpan.textContent = `${item.usersCount} users`

        li.appendChild(nameSpan)
        li.appendChild(badgeSpan)
        listContainer.appendChild(li)
      })
    }

    if (confirmInput) {
      confirmInput.value = ""
      confirmInput.placeholder = `Type ${keyword} to confirm`
      if (confirmBtn) {
        confirmBtn.disabled = true
        confirmBtn.textContent = isDelete ? "Confirm Deletion" : "Confirm Deactivation"
      }

      confirmInput.oninput = (e) => {
        if (confirmBtn) {
          confirmBtn.disabled = e.target.value.trim().toUpperCase() !== keyword
        }
      }
    }

    if (confirmBtn) {
      confirmBtn.onclick = () => {
        const bsModal = (window.bootstrap || bootstrap).Modal.getInstance(modalElement)
        if (bsModal) bsModal.hide()
        this.submit(event, true)
      }
    }

    const bsModal = (window.bootstrap || bootstrap).Modal.getOrCreateInstance(modalElement)
    bsModal.show()
  }

  storedMetadata() {
    try {
      const stored = sessionStorage.getItem(`${this.storageKeyValue}_metadata`)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  saveMetadata(metadata) {
    try {
      sessionStorage.setItem(`${this.storageKeyValue}_metadata`, JSON.stringify(metadata))
    } catch {
      // no-op
    }
  }

  syncCurrentPageMetadata() {
    const metadata = this.storedMetadata()
    const selectedIds = new Set(this.selectedIds().map(String))

    document.querySelectorAll("tr[data-test-id], tr[data-organization-id]").forEach(row => {
      const id = String(row.dataset.testId || row.dataset.organizationId)
      if (selectedIds.has(id)) {
        const name = row.dataset.organizationName || row.querySelector("td a")?.textContent?.trim() || `Organization #${id}`
        const usersCount = parseInt(row.dataset.usersCount || row.children[2]?.textContent?.trim() || "0", 10) || 0

        metadata[id] = { name, usersCount }
      }
    })

    Object.keys(metadata).forEach(id => {
      if (!selectedIds.has(id)) {
        delete metadata[id]
      }
    })

    this.saveMetadata(metadata)
  }
}
